import React, { createRef } from 'react';
import ReactDOM from 'react-dom';
// useSelector hook in commentsCount throws error if not wrapped in Provider 
import { connect, Provider } from 'react-redux';
import { store } from '../index.js';
import { bindActionCreators } from 'redux';
import * as L from 'leaflet';
import MapPopup from './mappopup';
import PropTypes from 'prop-types';
import { displayListingPrice } from './listingDetailsHelpers.js';
import { getFormattedPolygon } from './utils';
import 'leaflet'; // Creates a global L namespace.
import 'leaflet.markercluster'; // Attaches L.markerClusterGroup to global L.
import 'leaflet.smooth_marker_bouncing';
import 'leaflet.gridlayer.googlemutant';
import * as searchActions from '../actions/search';
import '../scss/shared/_map.scss';
import * as listingActions from '../actions/listings';
import { NTiledLayerWMS } from './noneTileLayerWms';
import 'leaflet/dist/leaflet.css';

// bing is used Internally by leaflet, it isn't referenced on the page
// eslint-disable-next-line
import * as bing from 'leaflet-bing-layer';
import { APIProvider } from '@vis.gl/react-google-maps';

class EsriLeafletMap extends React.Component {
    static contextTypes = {
        router: PropTypes.object.isRequired
    }
    constructor(props) {
        super(props);
        const lat = this.props.Latitude;
        const lng = this.props.Longitude;
        const zoom = this.props.Zoom || 6;
        const markers = (this.props.Latitude !== undefined && this.props.Longitude !== undefined) ? { LT: this.props.Latitude, LG: this.props.Longitude } : {};
        const geoserver_url = process.env.REACT_APP_GeoServerURI || ' ';
        const { layers = [] } = this.props;
        const { defaultView } = this.props;
        let blayers = {}

        for (let i = 0; i < layers.length; i++) {
            let options = {
                layers: layers[i].name,
                transparent: true,
                format: 'image/png',
                minZoom: layers[i].minZoom,
                maxZoom: layers[i].maxZoom
            };
            let q = layers[i].query;
            if (q) {
                options.cql_filter = q.field + " like '%" + q.value + "%'";
            }
            blayers[layers[i].id] = new NTiledLayerWMS(geoserver_url, options);
        }

        this.state = {
            lat: lat,
            lng: lng,
            zoom: zoom,
            mapBounds: null,
            mapObj: null,
            markersLayer: null,
            boundariesLayer: null,
            polygons: [],
            polygonControl: null,
            Drawing: false,
            isMobile: false,
            layers: blayers,
            current_layer: null,
            defaultView: defaultView,
            googleBaseLayers: []
        }
        this.onPolygonClick = this.onPolygonClick.bind(this);
        this.onPolygonHover = this.onPolygonHover.bind(this);
        this.refreshMap = this.refreshMap.bind(this);
        this.markers = markers;
        this.googleBaseLayers = []
        this.mapRef = createRef();
    }

    componentDidMount() {
        const self = this;
        const position = [this.state.lat, this.state.lng];
        const zoomLevel = this.state.zoom;
        const map = L.map(this.mapRef.current).setView(position, zoomLevel);
        const { defaultView } = this.state;
        map.attributionControl.remove();
        map.setMinZoom(this.state.minZoom);
        //disabling scroll wheel zoom based on CM-21132
        if (this.props.DisableScrollZoom) {
            map.scrollWheelZoom.disable();
        }
        map.createPane('fixed', this.mapRef.current);

        const lyrGroup = L.featureGroup().addTo(map);

        const getStyles = (s) => {
            return Object.entries(s || {}).map(([key, value]) => {
                return { featureType: value.featureType, stylers: [{ visibility: value.enabled ? 'on' : 'off' }] };
            });
        };
        const defaultStyle = getStyles(this.props.poiSelected);
        const road = L.gridLayer.googleMutant({ type: 'roadmap', styles: defaultStyle });
        const aerial = L.gridLayer.googleMutant({ type: 'satellite', styles: defaultStyle });
        const hybrid = L.gridLayer.googleMutant({ type: 'hybrid', styles: defaultStyle });
        road.on('spawned', ({ mapObject, target: layer }) => {
            this.googleBaseLayers.push({ mapObject, layer });
            mapObject.setOptions({ styles: getStyles(this.props.poiSelected) });
            layer.initialize(mapObject.options);
            setTimeout(() => layer.redraw(), 100);
        });
        aerial.on('spawned', ({ mapObject, target: layer }) => {
            this.googleBaseLayers.push({ mapObject, layer });
        });
        hybrid.on('spawned', ({ mapObject, target: layer }) => {
            this.googleBaseLayers.push({ mapObject, layer });
            mapObject.setOptions({ styles: getStyles(this.props.poiSelected) });
            layer.initialize(mapObject.options);
            setTimeout(() => layer.redraw(), 100);
        });

        switch (defaultView) {
            case 'hybrid':
                hybrid.addTo(map);
                break;
            case 'aerial':
                aerial.addTo(map);
                break;
            default:
                road.addTo(map);
        }
        var baseLayers = {
            "<span style='color:#0078A8;'>Road</span>": road,
            "<span style='color:#0078A8;'>Aerial</span>": aerial,
            "<span style='color:#0078A8;'>Hybrid</span>": hybrid
        };

        L.control.layers(baseLayers, {}, { position: 'bottomright' }).addTo(map);

        const lyrBoundaries = L.featureGroup().addTo(map);

        if (this.props.allowMapBoundUpdate) {
            this.registerZoom(map);
        }
        if (this.props.allowDraw) {
            this.createDrawButton(map);
            this.registerMouseEvents(map);
            this.registerTouchEvents(map);
        }
        if (this.props.allowLocate && navigator.geolocation) {
            this.createLocateButton(map);
        }
        if (this.props.allowLayers) {
            this.createLayerButton(map);
        }

        L.control.scale({ metric: false }).addTo(map);
        if (this.mapRef.current) {
            this.setState({ mapObj: map, markersLayer: lyrGroup, boundariesLayer: lyrBoundaries }, () => {
                if (Object.keys(self.markers).length !== 0) {
                    this.addMarkersToMap();
                }
            });
        }
        // TODO replace with withSizes
        if (window.innerWidth < 768) // show normal on ipad
            self.setState({ isMobile: true });
        window.addEventListener("resize", this.updateIsMobile);

        // remove "tabbing" for links inside the map
        setTimeout(() => {
            const elements = document.querySelectorAll(".leaflet-control a, .leaflet-container");
            for (let i = 0; i < elements.length; ++i) {
                elements[i].setAttribute("tabindex", "-1");
            }
        }, 500);
    }

    componentWillUnmount() {
        // Todo don't use Session use the Map
        window.removeEventListener("resize", this.updateIsMobile);
    }

    componentWillReceiveProps(nextProps) {
        // is polygon in search page url?
        if (nextProps.polygon && !this.state.polygons.length
            && JSON.stringify(nextProps.polygon) !== JSON.stringify(this.props.polygon)) {
            this.setState({ polygons: nextProps.polygon }, () => {
                const myPolygon = new L.Polygon(nextProps.polygon, { 'label': 'label' });
                myPolygon.setStyle({ fillColor: '#2E6EA1', color: '#2E6EA1' });
                myPolygon.addTo(this.state.mapObj);
                this.setState({ leafletPolygon: myPolygon });
            });
        }
        // if mappopup is open and listing updates do it
        else if (JSON.stringify(this.props.properties) !== JSON.stringify(nextProps.properties)
            && nextProps.properties.length
            && this.state.mapObj && this.state.mapObj._popup && this.state.mapObj._popup.isOpen()) {
            const self = this;
            const PopupWithContext = withContext(MapPopup, this.context);
            const popup = document.createElement('div');
            const updatedProperty = nextProps.properties.filter(p => p.Id === self.state.popupPropertyId)[0];
            ReactDOM.render(
                <Provider store={store}>
                    <PopupWithContext listingActions={self.props.listingActions} onCommentsClick={self.props.onCommentsClick} property={updatedProperty} history={self.props.history} />
                </Provider>
                , popup);
            self.state.mapObj._popup.options.minWidth = 300; // not sure why we have to set this. seems like a bug
            self.state.mapObj._popup.setContent(popup);
        }
        else if (this.props.bounceMarker !== nextProps.bounceMarker) {
            this.bounceMarker(nextProps.bounceMarker);
        }
    }

    shouldComponentUpdate(nextProps) {
        if (JSON.stringify(this.props.properties) !== JSON.stringify(nextProps.properties) ||
            this.props.layerSelected !== nextProps.layerSelected ||
            this.props.mapAdd !== nextProps.mapAdd ||
            JSON.stringify(this.props.poiSelected) !== JSON.stringify(nextProps.poiSelected) ||
            (this.props.clearPolygon !== nextProps.clearPolygon && nextProps.clearPolygon) ||
            this.props.height !== nextProps.height) {
            return true;
        }
        return false;
    }

    componentDidUpdate(prevProps) {
        const self = this;
        if (self.state.mapObj) {
            if (!window.sessionStorage.getItem("PreventMapMarkerPopupClose")) {
                self.state.mapObj.closePopup();
            }
            else {
                window.sessionStorage.removeItem("PreventMapMarkerPopupClose");
            }
        }
        if (self.state.markersLayer) {
            self.state.markersLayer.clearLayers();
        }

        if (this.props.allowDraw) {
            const polyControl = Object.assign(self.state.polygonControl);
            if (polyControl && polyControl !== null) {
                if (self.state.polygons && self.state.polygons.length > 0 && !this.props.clearPolygon) {
                    L.DomUtil.removeClass(polyControl, 'leaflet_icon_draw');
                    L.DomUtil.addClass(polyControl, 'leaflet_icon_draw_clear');
                    polyControl.title = "Remove drawing";
                }
                else {
                    L.DomUtil.removeClass(polyControl, 'leaflet_icon_draw_clear');
                    L.DomUtil.addClass(polyControl, 'leaflet_icon_draw');
                    polyControl.title = "Draw an area";
                    if (this.props.clearPolygon) {
                        this.exitMapDrawing(this.state.mapObj, document.getElementById('AddPolygon'), self);
                        //clear the polygon
                        self.onPolygonClick();
                        //reset the flag
                        this.props.onPolygonCleared();
                    }
                }
            }
        }

        let foundPoints = false;
        if (self.props.properties && self.state.mapObj && self.state.markersLayer) {
            foundPoints = this.addMarkersToMap();

            if (foundPoints || (self.props.searchParameters_Redux && self.props.searchParameters_Redux.geochords)) {
                self.state.mapObj.addLayer(self.state.markersLayer);
                if (self.props.searchParameters_Redux &&
                    self.props.searchParameters_Redux.geochords &&
                    !self.props.searchParameters_Redux.mapbound) {
                    const split = self.props.searchParameters_Redux.geochords.split(',');
                    const bounds = L.latLng(split[1], split[0]).toBounds(2000); // this creates shape around center point padded to meters
                    const geoBounds = L.latLngBounds(bounds._southWest, bounds._northEast);
                    window.sessionStorage.setItem("MapZoomSearch", "false");
                    self.state.mapObj.fitBounds(geoBounds);
                }
                else if (!self.props.searchFilters ||
                    (self.props.searchFilters && !self.props.searchFilters.Criteria.mapbound)) {
                    if (self.props.searchFilters && !self.props.searchFilters.cities) {
                        //This variable is used to control the auto zooming of map
                        window.sessionStorage.setItem("MapZoomSearch", "false");
                    }
                    if (self.props.fitBounds) self.state.mapObj.fitBounds(self.state.markersLayer.getBounds());
                }
                else {
                    // Use the mapbound parameter to fit the foundries.
                    const mp = self.props.searchFilters ? self.props.searchFilters.Criteria.mapbound.split(',') : [];
                    if (mp && mp.length === 2) {
                        const mpTL = mp[0].split(' ');
                        const mpBR = mp[1].split(' ');
                        if (mpTL && mpTL.length === 2 && mpBR && mpBR.length === 2) {
                            const corner1 = L.latLng(mpBR[0], mpTL[1]);
                            const corner2 = L.latLng(mpTL[0], mpBR[1]);
                            const mpll = L.latLngBounds(corner1, corner2);
                            //This variable is used to control the auto zooming of map
                            window.sessionStorage.setItem("MapZoomSearch", "false");
                            if (self.props.fitBounds) self.state.mapObj.fitBounds(mpll);
                        }
                    }
                }
            }
        }
        if (self.state.boundariesLayer) {
            self.state.boundariesLayer.clearLayers();
        }


        if (self.props.layerSelected && prevProps.layerSelected !== self.props.layerSelected) {
            self.setCurrentLayer(self.props.layerSelected, self.props.mapAdd);
        } else if (!self.props.layerSelected) {
            self.clearCurrentLayer(self.props.mapAdd)
        }
        if (JSON.stringify(self.props.poiSelected) !== JSON.stringify(prevProps.poiSelected)) {
            self.setPoiStyles(self.props.poiSelected)
        }
    }

    //--------------------------------------------------------------------------
    // polygon functions
    // -------------------------------------------------------------------------
    onPolygonHover(event) {
        if (event.type === 'mouseover' && event.target && event.target._tooltip && event.target._tooltip.options) {
            event.target._tooltip.options.permanent = true;
            event.target._tooltip.options.interactive = true;
        }
        if (event.type === 'mouseout' && event.target && event.target._tooltip && event.target._tooltip.options) {
            event.target._tooltip.options.permanent = false;
            event.target._tooltip.options.interactive = false;
        }
    }

    // Update the search parameters with the new polygon
    updatePolygonParameter = (polyObj) => {
        let parameters = Object.assign({}, this.props.searchFilters.Criteria);
        delete parameters["polygon"];
        if (polyObj && polyObj.length > 0) {
            let searchPoly = [];
            polyObj.map((eachPoly) => {
                searchPoly.push(getFormattedPolygon(eachPoly));
                return null;
            });
            if (searchPoly.length > 0) {
                parameters.polygon = searchPoly[0];
            }
        }
        this.props.searchActions.updateSearchFilters(Object.assign({}, this.props.searchFilters, { Criteria: parameters }));
    }

    // Event is called when we remove a polygon
    onPolygonClick(event) {
        const self = this;
        let currentPolygon = Object.assign(self.state.polygons, []);
        const leafletPolygon = self.state.leafletPolygon;

        // Remove the Leaflet Polygon object
        if (leafletPolygon) self.state.mapObj.removeLayer(leafletPolygon);

        if (currentPolygon && currentPolygon.length > 0) {
            currentPolygon = [];
        }
        self.setState({ polygons: currentPolygon });
        self.updatePolygonParameter(currentPolygon);

        if (currentPolygon) {
            if (self.props.onMapPolygoneChanged) {
                self.props.onMapPolygoneChanged(currentPolygon);
            }
        }
    }

    //---------------------------------------------------------------------------
    // marker functions
    // --------------------------------------------------------------------------
    addMarkersToMap() {
        const self = this;
        let foundPoints = false;
        const PopupWithContext = withContext(MapPopup, this.context);
        const groupMarkers = [];
        self.markers = {};
        self.props.properties.map(function (property, i) {
            if (property && property.Latitude !== undefined && property.Latitude !== null &&
                property.Longitude !== undefined && property.Longitude !== null &&
                property.Latitude !== 0 && property.Longitude !== 0) {
                let tooltip = property.StreetAddress + (property.City ? ', ' + property.City + ', ' + property.State : '') + (property.Zip ? ' ' + property.Zip : '');
                let isGroup = false;
                let isGroupExist = false;
                const groupLst = self.props.properties.filter(p => p.Latitude === property.Latitude && p.Longitude === property.Longitude);
                if (groupLst && groupLst.length > 1) {
                    const gExist = groupMarkers.filter(g => g.Latitude === property.Latitude && g.Longitude === property.Longitude);
                    if (gExist && gExist.length > 0) {
                        isGroupExist = true;
                    }
                    else {
                        groupMarkers.push({ LT: property.Latitude, LG: property.Longitude });
                    }
                    isGroup = true;
                    tooltip = groupLst.length + " Units";
                }

                if (!isGroupExist) {
                    const price = displayListingPrice({ ...property, ListPriceLow: null });
                    const pMarker = L.marker([property.Latitude, property.Longitude], {
                        icon: new L.DivIcon({
                            className: 'marker-text-wrapper',
                            html: '<div class="marker-text-inner"><div class="marker-text">' + price + '</div></div>'
                        }),
                        title: tooltip,
                        riseOnHover: true,
                        property: property,
                        isGroup: isGroup,
                        groupLst: (groupLst && groupLst.length > 1) ? groupLst : null,
                        keyboard: false
                    });
                    pMarker.on('popupopen', () => {
                        self.props?.onPopupOpen?.();
                    });
                    pMarker.on('popupclose', () => {
                        self.props?.onPopupClose?.();
                    });
                    pMarker.addTo(self.state.markersLayer);
                    self.markers[property.Id] = pMarker;
                    if (!self.props.doNotShowPopup) {
                        if (!self.state.isMobile) {
                            pMarker.on('click', function (e) {
                                if (self.props.searchParameters_Redux && !self.props.searchParameters_Redux.locationtext && !self.props.searchParameters_Redux.mapbound) {
                                    //This variable is used to control the auto zooming of map
                                    window.sessionStorage.setItem("MapZoomSearch", "false");
                                }
                                self.setState({ popupPropertyId: this.options.property.Id });
                                const mLatLng = this.getLatLng();
                                const mPoint = self.state.mapObj.latLngToContainerPoint(mLatLng);
                                mPoint.y = mPoint.y - 22;
                                mPoint.x = mPoint.x + 1;
                                const pLatLng = self.state.mapObj.containerPointToLatLng(mPoint);
                                const popup = document.createElement('div');
                                document.body.appendChild(popup); // append to body to avoid tooltip error
                                ReactDOM.render(
                                    <Provider store={store}>
                                        <PopupWithContext listingActions={self.props.listingActions} onCommentsClick={self.props.onCommentsClick} property={this.options.property} history={self.props.history} />
                                    </Provider>
                                    , popup);
                                L.popup().setLatLng(pLatLng).setContent(popup).openOn(self.state.mapObj);
                            });
                        }
                        else { // isMobile is true
                            const popup = document.createElement('div');
                            ReactDOM.render(
                                <Provider store={store}>
                                    <PopupWithContext listingActions={self.props.listingActions} onCommentsClick={self.props.onCommentsClick} property={pMarker.options.property} history={self.props.history} isMobile={self.state.isMobile} />
                                </Provider>
                                , popup);
                            const fixPopup = L.popup({
                                pane: 'fixed',
                                className: 'popup-fixed',
                                autoPan: false,
                            }).setContent(popup);
                            pMarker.bindPopup(fixPopup, { maxWidth: 560 });
                        }
                    }
                }
                foundPoints = true;
            }
            return (<div />);
        });
        return foundPoints;
    }

    bounceMarker(listingId) {
        L.Marker.stopAllBouncingMarkers();
        if (listingId && this.markers[listingId]) {
            this.markers[listingId].setBouncingOptions({
                bounceHeight: 5,
                exclusive: true
            }).bounce(3);
        }
    }

    // --------------------------------------------------------------------------
    // map and mobile
    // --------------------------------------------------------------------------
    createDrawButton(map) {
        const self = this;
        //Draw button----
        L.Control.PolygonTool = L.Control.extend({
            onAdd: function (map) {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet_icon_draw');
                container.style.backgroundColor = 'white';
                container.style.width = '30px';
                container.style.height = '30px';
                container.style.cursor = 'pointer';
                container.style.textAlign = 'center';
                container.style.lineHeight = '27px';
                container.id = 'AddPolygon';
                container.title = 'Draw an area';

                container.onclick = function (e) {
                    const isDrawing = !self.state.Drawing;
                    if (isDrawing) {
                        if (self.state.polygons && self.state.polygons.length > 0) {
                            self.onPolygonClick(e);
                        }
                        else {
                            map.dragging.disable();
                            L.DomUtil.addClass(map._container, 'crosshair-cursor-enabled');
                            container.style.backgroundColor = '#cccccc';
                            //container.style.backgroundColor = 'white';
                            container.title = "Cancel draw";
                            self.setState({ Drawing: true });
                        }
                    }
                    else {
                        self.exitMapDrawing(map, container, self);
                    }
                }

                self.setState({ polygonControl: container });
                return container;
            },
            onRemove: function (map) {
                // Nothing to do here
            }
        });

        L.control.PolygonTool = function (opts) { return new L.Control.PolygonTool(opts); }
        L.control.PolygonTool({ position: 'topleft' }).addTo(map);
        //----
    }

    exitMapDrawing(map, container, self) {
        map.dragging.enable();
        L.DomUtil.removeClass(map._container, 'crosshair-cursor-enabled');
        container.style.backgroundColor = 'white';
        container.title = 'Draw an area';
        self.setState({ Drawing: false });
    }

    createLocateButton(map) {
        const self = this;
        //Draw button----
        L.Control.LocateTool = L.Control.extend({
            onAdd: function (map) {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet_icon_locate');
                container.style.backgroundColor = 'white';
                container.style.width = '30px';
                container.style.height = '30px';
                container.style.cursor = 'pointer';
                container.style.textAlign = 'center';
                container.style.lineHeight = '27px';
                container.id = 'LocateMe';
                container.title = 'Find nearby properties';

                const onLocationFound = function (e) {
                    map.setView(e.latlng, 14);
                };
                map.on('locationfound', onLocationFound);

                const onLocationError = function (e) { alert(e.message); };
                map.on('locationerror', onLocationError);

                container.onclick = function (e) {
                    map.locate({ setView: false });
                }

                self.setState({ locateControl: container });
                return container;
            },
            onRemove: function (map) {
                // Nothing to do here
            }
        });

        L.control.LocateTool = function (opts) { return new L.Control.LocateTool(opts); }
        L.control.LocateTool({ position: 'topleft' }).addTo(map);
        //----
    }

    createLayerButton(map) {
        const self = this;
        L.Control.Layers = L.Control.extend({
            onAdd: function (onAddMap) {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet_icon_layer');
                container.id = 'Layer';
                container.title = 'Layers';
                container.onclick = function (e) {
                    self.props.openMenuLayers()
                }
                self.props.setMapAdd(onAddMap)
                self.setState({ layerControl: container });
                return container;
            },
        });
        L.control.Layers = function (opts) { return new L.Control.Layers(opts); }
        L.control.Layers({ position: 'topleft' }).addTo(map);
    }

    registerZoom(map) {
        const self = this;
        window.sessionStorage.setItem("WatchForZoom", "true");
        map.on('zoomend moveend', function (e) {
            if (window.sessionStorage.getItem("MapZoomSearch") !== null && window.sessionStorage.getItem("MapZoomSearch") !== "false") {
                //Whenever map auto zooms in/out, this function is called twice, 
                //first time with zoomed and second time with moveend event
                if (e && e.type === "moveend") {
                    window.sessionStorage.removeItem("MapZoomSearch");
                }
            }
            else {
                if (window.sessionStorage.getItem("WatchForZoom") === "true") {
                    const bounds = map.getBounds();
                    if (bounds && bounds._southWest && bounds._northEast) {
                        let parameters = Object.assign({}, self.props.searchFilters.Criteria);
                        const strBounds = bounds._northEast.lat + " " + bounds._southWest.lng +
                            "," + bounds._southWest.lat + " " + bounds._northEast.lng;
                        if (!parameters.mapbound || (parameters.mapbound && parameters.mapbound !== strBounds)) {
                            delete parameters["mapbound"];
                            parameters.mapbound = strBounds;
                            self.props.searchActions.updateSearchFilters(Object.assign({}, self.props.searchFilters, { Criteria: parameters }));
                        }
                    }
                }
            }
        });
    }

    registerMouseEvents(map) {

        // iOS mobile will register a touch as a mousedown and ontouchstart
        const isiOS = /iPad|iPhone|iPod/i.test(navigator.userAgent) && !window.MSStream;
        if (isiOS)
            return;

        const self = this;
        map.addEventListener('mousedown', function (ev) {
            if (!self.state.Drawing) {
                return;
            }
            // We are currently drawing a polygon
            const currentPolygon = Object.assign(self.state.polygons, []);
            const polygonToDraw = [];
            const searchPolygon = [];
            let myPolygon = null;
            let skip = 0;

            map.addEventListener('mousemove', function (ev) {
                skip++;
                if (skip % 3 !== 0) {
                    return;
                }
                polygonToDraw.push([ev.latlng.lat, ev.latlng.lng]);
                searchPolygon.push(ev.latlng);
                if (myPolygon !== null) {
                    map.removeLayer(myPolygon);
                }
                myPolygon = new L.Polygon(polygonToDraw, { 'label': 'label' });
                myPolygon.setStyle({ fillColor: '#2E6EA1', color: '#2E6EA1' });
                myPolygon.addTo(map);
                self.setState({ leafletPolygon: myPolygon });
            });

            map.addEventListener('mouseup', function (ev) {
                if (!self.state.Drawing) {
                    return;
                }
                map.removeEventListener('mousemove');
                map.removeEventListener('mouseup');

                self.drawFinishPolygon(self, ev, map);

            });

            currentPolygon.push(searchPolygon);
            self.setState({ polygons: currentPolygon });
        });


    }

    registerTouchEvents(map) {
        const self = this;
        const mapElement = this.mapRef.current;
        mapElement.addEventListener('touchstart', function (ev) {
            if (!self.state.Drawing) {
                return;
            }

            const currentPolygon = Object.assign(self.state.polygons, []);
            const polygonToDraw = [];
            const searchPolygon = [];
            let myPolygon = null;
            let skip = 0;
            mapElement.addEventListener('touchmove', function _touchmove(ev) {
                ev.preventDefault(); // prevent page scrolling on mobile

                skip++;
                if (skip % 3 !== 0) {
                    return;
                }

                if (!ev.changedTouches || ev.changedTouches.length === 0 ||
                    !ev.changedTouches[0] || !ev.changedTouches[0].clientX ||
                    !ev.changedTouches[0].clientY) {
                    return;
                }
                const rect = map._container.getBoundingClientRect(),
                    lat = ev.changedTouches[0].clientX - rect.left - map._container.clientLeft,
                    lng = ev.changedTouches[0].clientY - rect.top - map._container.clientTop,
                    containerPoint = L.point(lat, lng),
                    layerPoint = map.containerPointToLayerPoint(containerPoint),
                    latlng = map.layerPointToLatLng(layerPoint);

                polygonToDraw.push([latlng.lat, latlng.lng]);
                searchPolygon.push(latlng);

                if (myPolygon !== null) {
                    map.removeLayer(myPolygon);
                }

                myPolygon = new L.Polygon(polygonToDraw, { 'label': 'label' });
                myPolygon.setStyle({ fillColor: '#2E6EA1', color: '#2E6EA1' });
                myPolygon.addTo(map);
                self.setState({ leafletPolygon: myPolygon });

                // Store function to state for removal
                if (!self.state.touchFunction)
                    self.setState({ touchFunction: _touchmove });
            });

            currentPolygon.push(searchPolygon);
            self.setState({ polygons: currentPolygon });

            // Handle default Leaflet touch events
            mapElement.addEventListener('touchend', function _touchend(ev) {
                if (!self.state.Drawing) {
                    return;
                }
                mapElement.removeEventListener('touchend', _touchend);

                // Remove function from state variable
                mapElement.removeEventListener('touchmove', self.state.touchFunction);
                self.setState({ touchFunction: undefined });

                self.drawFinishPolygon(self, ev, map);
            });

        });
    }

    drawAddPoint(self, ev, map, polygonToDraw, searchPolygon, myPolygon, currentPolygon) {
    }

    drawFinishPolygon(self, ev, map) {
        if (!self.hasElement(ev.srcElement, 'AddPolygon')) {
            map.dragging.enable();
            L.DomUtil.removeClass(map._container, 'crosshair-cursor-enabled');
            const polyControl = Object.assign(self.state.polygonControl, {});
            if (polyControl != null) {
                polyControl.style.backgroundColor = 'white';
            }
            // if they toggle map draw without drawing anything leave Drawing true (gets updated in container.onclick)
            if (self.state.polygons.length === 1 && self.state.polygons[0].length !== 0)
                self.setState({ Drawing: false, polygonControl: polyControl });
        }

        const polygonToRemove = Object.assign(self.state.polygons, {});
        let needReload = true;

        for (let index = 0; index < polygonToRemove.length; index++) {
            if (polygonToRemove[index].length < 3) {
                needReload = false;
                polygonToRemove.splice(index, 1);
            }
        }

        self.setState({ polygons: polygonToRemove });
        if (needReload && self.props.onMapPolygoneChanged) {
            self.props.onMapPolygoneChanged(polygonToRemove);
        }

        self.updatePolygonParameter(polygonToRemove);
    }

    refreshMap() {
        if (this.state.mapObj) { this.state.mapObj.invalidateSize() };
    }

    setPoiStyles(poiFeatures) {
        const poiStyles = Object.entries(poiFeatures).map(([key, value]) => {
            return { featureType: value.featureType, stylers: [{ visibility: value.enabled ? 'on' : 'off' }] };
        });
        const { mapObj: map } = this.state;
        this.googleBaseLayers.forEach(({ mapObject, layer }) => {
            mapObject.setOptions({ styles: poiStyles });
            if (layer.options.type === 'satellite') return;
            layer.initialize(mapObject.options);
            if (map?.hasLayer(layer)) {
                layer.redraw();
            }

        });
    }

    setCurrentLayer(which, map) {
        const self = this;

        if (self.state.current_layer) {
            self.state.mapObj.removeLayer(self.state.current_layer);
            self.state.current_layer = null;
        }

        self.state.current_layer = self.state.layers[which];
        if (self.state.current_layer)
            self.state.current_layer.addTo(map);
    }

    clearCurrentLayer(map) {
        const self = this;
        if (self.state.current_layer) {
            map.removeLayer(self.state.current_layer);
            self.state.current_layer = null;
        }
    }

    clearMap() {
        let self = this;
        const localMap = self.state.mapObj;
        if (!localMap) return;

        for (const index in localMap._layers) {
            if (localMap._layers[index]._path !== undefined) {
                try {
                    localMap.removeLayer(localMap._layers[index]);
                }
                catch (e) {
                    console.log("problem with " + e + localMap._layers[index]);
                }
            }
        }
    }

    updateIsMobile = () => {
        this.setState({ isMobile: window.innerWidth < 768 });
    }

    hasElement(element, elementId) {
        return element && element.id === elementId;
    }

    resetMap = () => {
        const self = this;
        if (self.state.mapObj) {
            self.state.mapObj.fitBounds([
                [42.461966608980134, -73.44909667968751],
                [40.6723059714534, -72.11700439453126]
            ]);
        }
    }

    render() {
        const mapHeight = (this.props.height) ? { height: this.props.height } : {};
        const id = this.props.doNotShowPopup ? 'map-report' : 'map-view'; // prevent dupe ids
        //@TODO: move apiKey to env 
        return <APIProvider apiKey='AIzaSyB-VjmQn0uWRjZxmjOpcE6JsEuTRJ6bF8k'>
            <div style={{ touchAction: 'none' }}>
                <div id={id} ref={this.mapRef} className="map-company" style={mapHeight}></div>
            </div>
        </APIProvider>
    }
}

function withContext(WrappedComponent, context) {
    class ContextProvider extends React.Component {
        getChildContext() {
            return context;
        }
        render() {
            return <WrappedComponent {...this.props} />
        }
    }
    ContextProvider.childContextTypes = {};
    Object.keys(context).forEach(key => {
        ContextProvider.childContextTypes[key] = PropTypes.any.isRequired;
    });
    return ContextProvider;
}

const mapStateToProps = (state, props) => ({
    searchFilters: (state.search && state.search.filters) || {},
});

const mapDispatchToProps = dispatch => ({
    listingActions: bindActionCreators(listingActions, dispatch),
    searchActions: bindActionCreators(searchActions, dispatch)
});

const { lat: defaultLat = 29.4262853, lng: defaultLng = -93.2650 } = JSON.parse(process.env.REACT_APP_Map_Default_Location ?? "{}");

EsriLeafletMap.defaultProps = {
    Latitude: defaultLat,
    Longitude: defaultLng
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(EsriLeafletMap);
